import React from "react";
import { Link } from "react-router-dom";
import { Check, Crown, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PRICING_TIERS, FAQS } from "../lib/mockData";

const FEATURE_MATRIX = [
  { feat: "Innovation briefs / month", starter: "1", pro: "Unlimited", enterprise: "Unlimited" },
  { feat: "Quotes per brief",          starter: "Up to 3", pro: "10+", enterprise: "Unlimited" },
  { feat: "Attorney response time",     starter: "< 24 hrs", pro: "< 1 hr", enterprise: "< 30 min" },
  { feat: "Prior-art search",           starter: "Basic", pro: "Included", enterprise: "Included + report" },
  { feat: "Filing tracker & dashboard", starter: false, pro: true,  enterprise: true },
  { feat: "Document vault",             starter: false, pro: true,  enterprise: true },
  { feat: "1:1 consultation hours",      starter: false, pro: "2 / month", enterprise: "Unlimited" },
  { feat: "PCT international filing",    starter: false, pro: "Add-on", enterprise: "Included" },
  { feat: "Dedicated IP success manager",starter: false, pro: false, enterprise: true },
  { feat: "Volume discount rate cards",  starter: false, pro: false, enterprise: true },
  { feat: "Whitelabel innovator portal", starter: false, pro: false, enterprise: true },
  { feat: "Custom SLAs & NDAs",          starter: false, pro: false, enterprise: true },
];

export default function Pricing() {
  return (
    <div className="surface-cream min-h-screen" data-testid="pricing-page">
      <Header/>

      {/* HERO */}
      <section className="bg-white border-b border-[#E1E8E2] py-16">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 text-center">
          <div className="chip inline-flex mb-4">Pricing</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#0A2520] mb-4 leading-tight">Simple, transparent pricing.</h1>
          <p className="text-lg text-[#5A6B66] max-w-2xl mx-auto">Innovators always pay attorneys directly through escrow. Membership gives you priority, depth, and reach.</p>
        </div>
      </section>

      {/* TIERS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {PRICING_TIERS.map((t) => (
            <div key={t.name} className={`card p-8 relative ${t.highlight ? "ring-2 ring-[#0E6E5E] shadow-xl" : ""}`}>
              {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip chip-dark">Most popular</div>}
              <div className="font-display font-bold text-xl text-[#0A2520] mb-1">{t.name}</div>
              <div className="text-sm text-[#5A6B66] mb-6">{t.desc}</div>
              <div className="mb-7">
                <span className="font-display font-extrabold text-5xl text-[#0A2520]">
                  {typeof t.price === "number" ? (t.price === 0 ? "₹0" : `₹${t.price.toLocaleString('en-IN')}`) : t.price}
                </span>
                <span className="text-sm text-[#5A6B66] ml-2">{t.period}</span>
              </div>
              <ul className="space-y-3 mb-7">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#3D4F4A]">
                    <Check size={16} className="text-[#0E6E5E] flex-shrink-0 mt-0.5"/> {f}
                  </li>
                ))}
              </ul>
              <button className={t.highlight ? "btn-primary w-full justify-center" : "btn-outline w-full justify-center"}>{t.cta}</button>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-3 text-center">Compare every feature</h2>
        <p className="text-[#5A6B66] text-center mb-10">Side-by-side breakdown of what's in each plan.</p>

        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-[#0A2520] text-white">
                  <th className="text-left p-5 font-display font-bold">Feature</th>
                  <th className="p-5 font-display font-bold text-center">Starter</th>
                  <th className="p-5 font-display font-bold text-center bg-[#0E6E5E]">Pro</th>
                  <th className="p-5 font-display font-bold text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((row, i) => (
                  <tr key={row.feat} className={`border-t border-[#F0F4F0] ${i % 2 === 0 ? "bg-white" : "bg-[#FAFCFA]"}`}>
                    <td className="p-4 font-medium text-[#0A2520]">{row.feat}</td>
                    <td className="p-4 text-center text-[#5A6B66]">{cell(row.starter)}</td>
                    <td className="p-4 text-center text-[#0A2520] font-semibold bg-[#EBF3EC]">{cell(row.pro)}</td>
                    <td className="p-4 text-center text-[#5A6B66]">{cell(row.enterprise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CONCIERGE UPSELL */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="card-flat p-8 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 surface-deep text-white border-[#0A2520]">
          <div>
            <div className="chip chip-gold inline-flex mb-4"><Crown size={12}/> Want the full white-glove?</div>
            <h3 className="font-display font-extrabold text-3xl sm:text-4xl mb-4 leading-tight">Concierge handles every step — for a single fixed price.</h3>
            <p className="text-white/70 mb-7 max-w-xl leading-relaxed">For founders, scientists, and corporates who want the entire patent journey delegated. We handle eligibility, drafting, filing, office actions, and renewal — under one engagement.</p>
            <Link to="/concierge" className="btn-accent">Explore Concierge <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-2 gap-4 self-center">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="font-display font-extrabold text-3xl text-[#7CC242] mb-1">₹29,500</div>
              <div className="text-xs text-white/70">Essential filing</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <div className="font-display font-extrabold text-3xl text-[#7CC242] mb-1">₹69,000</div>
              <div className="text-xs text-white/70">Full concierge</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 col-span-2">
              <div className="font-display font-extrabold text-3xl text-[#7CC242] mb-1">₹2,49,000 / yr</div>
              <div className="text-xs text-white/70">Founder's Office retainer</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <div className="text-center mb-10">
          <div className="chip inline-flex mb-3">Frequently asked</div>
          <h2 className="font-display font-bold text-3xl text-[#0A2520]">Questions about pricing?</h2>
        </div>
        <div className="space-y-3 max-w-3xl mx-auto">
          {FAQS.map((f) => (
            <details key={f.q} className="card p-5 group">
              <summary className="font-display font-semibold text-[#0A2520] cursor-pointer flex items-center justify-between gap-3">
                <span>{f.q}</span>
                <svg className="transition-transform group-open:rotate-180 flex-shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </summary>
              <p className="text-sm text-[#5A6B66] mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function cell(v) {
  if (v === true) return <Check size={16} className="inline text-[#0E6E5E]"/>;
  if (v === false) return <span className="text-[#D1D9D2]">—</span>;
  return v;
}
